import React, { useState, useEffect, useCallback } from "react";
import axios from "@/features/axiosHelper";
import _ from "lodash";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Pagination,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  ShepPagination,
  ShepCheckbox,
  ShepDataGridPro,
  ShepHorizontalMenu,
  ShepSearchBar,
} from "@/components/GenericComponents/ShepComponents";
import { ArrowDownInactiveComponent, ResultError } from "@/ImageRepo";
import LoadingBar from "@/components/shared/LoadingBar";
import { createAlert } from "@/features/homepage/alertSliceList";
import EmailAddItemPopup from "./AddCatalogs/EmailAddItemPopup";
import EmailAddItemUOMPopup from "./AddCatalogs/EmailAddItemUOMPopup";
import EmailAddAllowanceChargePopup from "./AddCatalogs/EmailAddAllowanceChargePopup";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import Dropdown from "@/components/Dropdown/Dropdown";
import EmailAddTaxGroupPopup from "./AddCatalogs/EmailAddTaxGroupPopup";

const FieldPropertyRow = ({
  label,
  path,
  value,
  onChange: onFieldUpdate,
  type = "text",
  isReadOnly = false,
  isRequired = false,
  showErrors = false,
  isConnectionDependent = false,
  connectionId,
}) => {
  const isError = isRequired && showErrors && (value === "" || value === null);
  const formattedValue =
    type === "date" && value ? value.substring(0, 10) : value || "";

  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{
          width: "280px",
          color: "var(--text-primary)",
          fontSize: "18px",
        }}
      >
        {label}
        {isRequired && (
          <span
            style={{
              color: "var(--error-primary)",
              verticalAlign: "super",
              marginLeft: "4px",
            }}
          >
            *
          </span>
        )}
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          type={type}
          value={formattedValue}
          onChange={(e) => onFieldUpdate(path, e.target.value)}
          disabled={
            isReadOnly || (isConnectionDependent && connectionId === -1)
          }
          error={isRequired && (value === "" || value === null)}
          helperText={isError ? "This field is required" : ""}
        />
      </TableCell>
    </TableRow>
  );
};

//fetch 15 catalogs from selected connection
//recycling thunk and API from connection grid
const fetchCatalogs = async (
  connId = -1,
  catalogArtifactType,
  count = 15,
  search = "",
  parentCatalogId = null,
) => {
  if (connId !== -1 && connId != null) {
    try {
      const response = await axios.get("/email/catalogs", {
        params: {
          connectionId: connId,
          dataType: catalogArtifactType,
          maxCount: count,
          search,
          parentCatalogId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching catalogs: ", error);
      return { Records: [] };
    }
  }
  return { Records: [] };
};

const filter = createFilterOptions({
  stringify: (option) => option.Code || option.title || "",
});

//for catalogs fields only
const BaseCatalogAutocomplete = ({
  value,
  catalogId,
  onChange,
  connectionId,
  artifactType,
  initialOptions = [],
  onAddNew,
  isDisabled = false,
  isError = false,
  helperText = "",
  variant = "outlined",
  autoFocus = false,
  parentCatalogId = null,
}) => {
  const [options, setOptions] = useState([]);
  const [returnedSearchVal, setReturnedSearchVal] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  const fetchAndSetOptions = async (params) => {
    const data = await fetchCatalogs(
      params.connectionId,
      params.artifactType,
      15,
      params.searchString,
      parentCatalogId,
    );
    setOptions(data?.Records || []);
    setReturnedSearchVal(params.searchString); //defined after backend finishes, later used to determine if add new option should appear
    setIsSearchLoading(false);
  };

  const debouncedSearch = useCallback(
    _.debounce((params) => fetchAndSetOptions(params), 500),
    [],
  );

  return (
    <Autocomplete
      sx={{ width: "100%" }}
      disabled={isDisabled}
      options={options}
      loading={isSearchLoading}
      loadingText="Searching..."
      onOpen={() => {
        if (value && typeof value === "string" && value.trim() !== "") {
          setIsSearchLoading(true);
          setOptions([]);
          fetchAndSetOptions({
            connectionId,
            artifactType,
            searchString: value,
          });
        } else {
          debouncedSearch.cancel();
          setOptions(initialOptions);
          setReturnedSearchVal("");
        }
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof val === "string") return option.Code === val;
        return option.Code === val.Code;
      }}
      filterOptions={(options, params) => {
        //to transition to the add catalog popup
        const filtered = filter(options, params);
        const { inputValue } = params;
        //check if current value is an existing catalog
        const isExisting = options.some((option) => inputValue === option.Code);
        if (
          inputValue !== "" &&
          !isExisting &&
          onAddNew &&
          inputValue === returnedSearchVal
        ) {
          filtered.push({
            inputValue,
            isNewOption: true,
            title: `+ Add "${inputValue}" as New Catalog`,
          });
        }
        return filtered;
      }}
      getOptionLabel={(option) => {
        if (option.Code == "" && option.CatalogId) return "[BLANK]";
        if (typeof option === "string") return option;
        if (option.isNewOption) return option.inputValue;
        return option.Code || "";
      }}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <li key={key} {...restProps}>
            {option.isNewOption ? (
              <strong style={{ fontWeight: 700, color: "var(--primary-main)" }}>
                {option.title}
              </strong>
            ) : option.Code == "" && option.CatalogId ? (
              "[BLANK]"
            ) : (
              option.Code
            )}
          </li>
        );
      }}
      value={(value === "" && catalogId ? "[BLANK]" : value) || null}
      //fired when the user clicks an option from the dropdown list
      onChange={(event, newValue) => {
        if (typeof newValue === "string") {
          onChange(newValue, null);
        } else if (newValue && newValue.isNewOption) {
          onAddNew(newValue.inputValue);
        } else {
          onChange(newValue);
        }
      }}
      //fired when the user physically types in the box
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          if (newInputValue.length > 0) {
            setIsSearchLoading(true);
            //give a bit of leeway before req backend
            debouncedSearch({
              connectionId,
              artifactType,
              searchString: newInputValue,
            });
          } else if (newInputValue.length === 0) {
            debouncedSearch.cancel();
            setIsSearchLoading(false);
            setOptions(initialOptions);
          }
        } else if (reason === "clear") {
          debouncedSearch.cancel();
          setIsSearchLoading(false);
          onChange("");
          setOptions(initialOptions);
          setReturnedSearchVal("");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="small"
          variant={variant}
          autoFocus={autoFocus}
          error={isError}
          helperText={helperText}
          sx={variant === "standard" ? { paddingLeft: "4px" } : {}}
        />
      )}
    />
  );
};

//for rows only
const CatalogAutocompleteRow = ({
  label,
  path,
  value,
  onChange,
  connectionId,
  artifactType,
  isRequired = false,
  showErrors = false,
  initialOptions = [],
  onAddNew,
  catalogId,
  parentCatalogId,
  disabledOverride,
  helperTextOverride,
}) => {
  const isConnectionDisabled = connectionId === -1 || connectionId == null;
  const isError =
    isRequired &&
    showErrors &&
    ((value === "" && catalogId == null) || value === null);

  const isDisabled =
    disabledOverride !== undefined ? disabledOverride : isConnectionDisabled;
  const helperText =
    helperTextOverride !== undefined
      ? helperTextOverride
      : isConnectionDisabled
        ? "Please select a connection first"
        : isError
          ? "This field is required"
          : "";

  return (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ width: "280px", color: "var(--text-primary)", fontSize: "18px" }}
      >
        {label}
        {isRequired && (
          <span
            style={{
              color: "var(--error-primary)",
              verticalAlign: "super",
              marginLeft: "4px",
            }}
          >
            *
          </span>
        )}
      </TableCell>
      <TableCell>
        <BaseCatalogAutocomplete
          value={value}
          catalogId={catalogId}
          onChange={(newValue) => onChange(path, newValue)}
          connectionId={connectionId}
          artifactType={artifactType}
          initialOptions={initialOptions}
          onAddNew={onAddNew}
          isDisabled={isDisabled}
          isError={isError}
          helperText={helperText}
          variant="outlined"
          parentCatalogId={parentCatalogId}
        />
      </TableCell>
    </TableRow>
  );
};

//for datagrid autocomplete cells
const GridCatalogAutocomplete = ({
  params,
  catalogId,
  connectionId,
  artifactType,
  initialOptions = [],
  isRequired = false,
  showErrors = false,
  onChange,
  onAddNew,
}) => {
  const isError =
    isRequired && showErrors && (params.value === "" || params.value === null);

  return (
    <BaseCatalogAutocomplete
      value={params.value}
      onChange={(newValue) => {
        onChange(newValue);
      }}
      catalogId={catalogId}
      isError={isError}
      connectionId={connectionId}
      artifactType={artifactType}
      initialOptions={initialOptions}
      onAddNew={(typedValue) => {
        params.api.stopCellEditMode({
          id: params.id,
          field: params.field,
          ignoreModifications: true,
        });
        onAddNew(typedValue);
      }}
      variant="standard"
      autoFocus={true}
    />
  );
};

const EditableArtifact = ({
  artifactData,
  onChange: onFieldUpdate,
  isDataLoaded,
  connectionId,
  connectionName,
  showValidationErrors,
  isDisabled,
}) => {
  //Header consts
  const dispatch = useDispatch();
  const headerData = artifactData?.DocumentHeader || {};
  const taxData = headerData.Taxes || [];
  const allowanceChargeData = headerData.MiscAllowanceCharges || [];
  const optFieldData = headerData.OptionalFields || [];
  const [activePopupData, setActivePopupData] = useState(null); //for grid only
  const [activePopupType, setActivePopupType] = useState(null); //state value = data type of catalog being added
  const [activeItemCatalogId, setActiveItemCatalogId] = useState(null);

  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [selectedAllowanceIds, setSelectedAllowanceIds] = useState([]);
  const [selectedOptFieldIds, setSelectedOptFieldIds] = useState([]);

  //Issue a distinct row id
  const getTaxId = (row) => row.TempId || row.TaxCode || row.LineNo;
  const getAllowanceId = (row) => row.TempId || row.ACCode || row.LineNo;
  const getOptionalFieldId = (row) => row.TempId || row.FieldCode || row.LineNo;
  // const getComponentId = (row) => row.TempId || row.ComponentCode || row.ComponentNumber;

  //Detail consts
  const detailsData = artifactData?.DocumentDetails || [];
  const [currentDetailPage, setCurrentDetailPage] = useState(1);

  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [detailSearchTerm, setDetailSearchTerm] = useState("");
  const [catalogOptions, setCatalogOptions] = useState({
    items: [],
    allowances: [],
    UOM: [],
    customers: [],
    shipTo: [],
    carriers: [],
    warehouses: [],
    taxes: [],
  });

  const filteredDetails = detailsData.filter((item) => {
    const searchStr = detailSearchTerm.toUpperCase();
    const itemCodeVals = item.ItemCode?.toUpperCase() || "";
    const descVals = item.ItemDescription?.toUpperCase() || "";
    return itemCodeVals.includes(searchStr) || descVals.includes(searchStr);
  });

  //For ensuring user doesn't end up on a blank page if a search shrinks the array
  const safePageCount = Math.max(1, filteredDetails.length);
  const safeCurrentPage = Math.min(currentDetailPage, safePageCount);
  const currentDetail = filteredDetails[safeCurrentPage - 1];

  //Find the item's original index in the full array so saves go to the right place
  const actualIndex = currentDetail ? detailsData.indexOf(currentDetail) : -1;

  // const detailComponents = currentDetail?.ItemComponents || [];
  const detailAllowances = currentDetail?.MiscAllowanceCharges || [];
  const detailTaxes = currentDetail?.Taxes || [];
  const detailOptFields = currentDetail?.OptionalFields || [];

  // const [selectedDetailComponentIds, setSelectedDetailComponentIds] = useState([]);
  const [selectedDetailAllowanceIds, setSelectedDetailAllowanceIds] = useState(
    [],
  );
  const [selectedDetailTaxIds, setSelectedDetailTaxIds] = useState([]);
  const [selectedDetailOptFieldIds, setSelectedDetailOptFieldIds] = useState(
    [],
  );

  const handleGridEdit = (
    newRow,
    oldRow,
    arrayPath,
    currentArray,
    getRowId,
  ) => {
    const updatedArray = [...currentArray];
    const index = updatedArray.findIndex(
      (row) => getRowId(row) === getRowId(oldRow),
    );
    if (index !== -1) {
      updatedArray[index] = newRow;
      onFieldUpdate(arrayPath, updatedArray);
    }
    return newRow;
  };

  const deleteConfirmation = (selectedIds, entityName, deleteAction) => {
    if (selectedIds.length > 0) {
      const isSingular = selectedIds.length === 1;
      const title = isSingular
        ? `Delete ${entityName} record?`
        : `Delete ${selectedIds.length} ${entityName} records?`;

      const message = isSingular
        ? `Are you sure you want to delete this ${entityName.toLowerCase()} row?`
        : `Are you sure you want to delete these ${entityName.toLowerCase()} rows?`;

      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: title,
          message: message,
          buttonLabel: "Yes",
          buttonLabel2: "No",
          onButtonClick: deleteAction,
        }),
      );
    } else {
      dispatch(
        createAlert({
          status: "Error",
          message: "Please select the records you want to delete first.",
        }),
      );
    }
  };

  const reIndexLines = (array, indexVar = "LineNo") => {
    if (!array || !Array.isArray(array)) return array;
    return array.map((row, index) => ({
      ...row,
      [indexVar]: index + 1,
    }));
  };

  const getInitialCatalogData = async () => {
    if (connectionId === -1 || !connectionId) return;

    try {
      const [
        itemData,
        allowanceData,
        cutsomerData,
        carrierData,
        warehouseData,
        taxData,
      ] = await Promise.all([
        fetchCatalogs(connectionId, CatalogTypes.Item, 15, ""),
        fetchCatalogs(connectionId, CatalogTypes.AllowanceOrCharge, 15, ""),
        fetchCatalogs(connectionId, CatalogTypes.Customer, 15, ""),
        fetchCatalogs(connectionId, CatalogTypes.Carrier, 15, ""),
        fetchCatalogs(connectionId, CatalogTypes.Warehouse, 15, ""),
        fetchCatalogs(connectionId, CatalogTypes.TaxGroup, 15, ""),
      ]);
      setCatalogOptions({
        items: itemData?.Records || [],
        allowances: allowanceData?.Records || [],
        customers: cutsomerData?.Records || [],
        carriers: carrierData?.Records || [],
        warehouses: warehouseData?.Records || [],
        taxes: taxData?.Records || [],
      });
    } catch (error) {
      console.error("Failed to fetch initial catalogs", error);
    }
  };

  useEffect(() => {
    getInitialCatalogData();
  }, [connectionId]);

  //automatically fetch UOMs when the detail page changes or item code is saved
  useEffect(() => {
    const loadUOMData = async () => {
      if (connectionId !== -1) {
        const uomData = await fetchCatalogs(
          connectionId,
          CatalogTypes.ItemUOM,
          15,
          "",
          currentDetail?.ItemCatalogId ?? null,
        );
        setCatalogOptions((prev) => ({
          ...prev,
          UOM: uomData?.Records || [],
        }));
        setActiveItemCatalogId(currentDetail?.ItemCatalogId || null);
      } else {
        setCatalogOptions((prev) => ({ ...prev, UOM: [] }));
        setActiveItemCatalogId(null);
      }
    };
    loadUOMData();
  }, [currentDetail?.ItemCode, currentDetail?.ItemCatalogId]);
  //automatically fetch UOMs when the detail page changes or item code is saved
  useEffect(() => {
    const loadShipToData = async () => {
      if (connectionId !== -1) {
        const shipToData = await fetchCatalogs(
          connectionId,
          CatalogTypes.ShipTo,
          15,
          "",
          headerData?.CustomerCatalogId ?? null,
        );
        setCatalogOptions((prev) => ({
          ...prev,
          shipTo: shipToData?.Records || [],
        }));
      } else {
        setCatalogOptions((prev) => ({ ...prev, shipTo: [] }));
      }
    };
    loadShipToData();
  }, [headerData?.CustomerCode, headerData?.CustomerCatalogId]);

  //Header handlers start here
  const handleAddAllowance = (e) => {
    if (e) e.stopPropagation();
    const newAllowanceRow = {
      TempId: `NEW-${Date.now()}`,
      LineNo: allowanceChargeData.length + 1,
      ACIndicator: "",
      ReferenceId: "",
      Amount: "",
      Percent: "",
      ACCode: "",
      MiscAllowanceChargeCatalogId: null,
      ACCodeDescription: "",
      Description: "",
      Rate: 0,
      UoM: "",
      PostType: "",
      AgenceACCode: "",
    };
    onFieldUpdate("DocumentHeader.MiscAllowanceCharges", [
      ...allowanceChargeData,
      newAllowanceRow,
    ]);
  };

  const handleDeleteAllowance = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedAllowanceIds, "Allowance/Charge", () => {
      const updatedAllowances = allowanceChargeData.filter(
        (row) => !selectedAllowanceIds.includes(getAllowanceId(row)),
      );
      const reIndexedAllowances = reIndexLines(updatedAllowances, "LineNo");
      onFieldUpdate("DocumentHeader.MiscAllowanceCharges", reIndexedAllowances);
      setSelectedAllowanceIds([]);
    });
  };

  const allowanceActions = [
    {
      label: "Delete",
      onClick: handleDeleteAllowance,
      variant: "danger",
      disabled: isDisabled,
    },
    { label: "Add Row", onClick: handleAddAllowance, disabled: isDisabled },
  ];

  const handleAddTax = (e) => {
    if (e) e.stopPropagation();
    const newTaxRow = {
      TempId: `NEW-${Date.now()}`,
      TaxCode: "",
      TaxAmount: 0,
      TaxName: "",
      CatalogId: null,
    };
    onFieldUpdate("DocumentHeader.Taxes", [...taxData, newTaxRow]);
  };

  const handleDeleteTax = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedTaxIds, "Tax", () => {
      const updatedTaxes = taxData.filter(
        (row) => !selectedTaxIds.includes(getTaxId(row)),
      );
      onFieldUpdate("DocumentHeader.Taxes", updatedTaxes);
      setSelectedTaxIds([]);
    });
  };

  const taxActions = [
    {
      label: "Delete",
      onClick: handleDeleteTax,
      variant: "danger",
      disabled: isDisabled,
    },
    { label: "Add Row", onClick: handleAddTax, disabled: isDisabled },
  ];

  const handleAddOptField = (e) => {
    if (e) e.stopPropagation();
    const newOptFieldRow = {
      TempId: `NEW-${Date.now()}`,
      FieldCode: "",
      FieldName: "",
      FieldValue: "",
    };
    onFieldUpdate("DocumentHeader.OptionalFields", [
      ...optFieldData,
      newOptFieldRow,
    ]);
  };

  const handleDeleteOptField = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedOptFieldIds, "Optional Field", () => {
      const updatedOptFields = optFieldData.filter(
        (row) => !selectedOptFieldIds.includes(getOptionalFieldId(row)),
      );
      onFieldUpdate("DocumentHeader.OptionalFields", updatedOptFields);
      setSelectedOptFieldIds([]);
    });
  };

  const optFieldActions = [
    {
      label: "Delete",
      onClick: handleDeleteOptField,
      variant: "danger",
      disabled: isDisabled,
    },
    { label: "Add Row", onClick: handleAddOptField, disabled: isDisabled },
  ];

  //Detail handlers start here
  const handleAddDetailPage = () => {
    const newDetail = {
      TempId: `NEW-DETAIL-${Date.now()}`,
      LineNumber: detailsData.length + 1,
      ItemCode: "",
      ItemCatalogId: null,
      ItemDescription: "",
      UoM: "",
      ItemUOMCatalogId: null,
      Quantity: "",
      UnitPrice: "",
      ExtendedPrice: 0,
      ShipFrom: {},
      // ItemComponents: [],
      MiscAllowanceCharges: [],
      Taxes: [],
      OptionalFields: [],
    };
    onFieldUpdate("DocumentDetails", [...detailsData, newDetail]);
    setDetailSearchTerm("");
    setLocalSearchTerm("");
    setCurrentDetailPage(detailsData.length + 1);
  };

  const handleDeleteDetailPage = () => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "Delete Line Item Page?",
        message:
          "Are you sure you want to delete this entire page? This will also delete all associated lists.",
        buttonLabel: "Yes",
        buttonLabel2: "No",
        onButtonClick: () => {
          const updatedDetails = detailsData.filter(
            (_, index) => index !== actualIndex,
          );
          const reIndexedDetails = reIndexLines(updatedDetails, "LineNumber");
          onFieldUpdate("DocumentDetails", reIndexedDetails);

          //Shift pagination back if user deleted the last page
          if (
            currentDetailPage > updatedDetails.length &&
            updatedDetails.length > 0
          ) {
            setCurrentDetailPage(updatedDetails.length);
          } else if (updatedDetails.length === 0) {
            setCurrentDetailPage(1);
          }
        },
      }),
    );
  };

  //Only show delete if there's actually a page to delete
  const detailPageActions =
    actualIndex !== -1
      ? [
          {
            label: "Delete Line",
            onClick: handleDeleteDetailPage,
            variant: "danger",
            disabled: isDisabled,
          },
          {
            label: "Add New Line",
            onClick: handleAddDetailPage,
            disabled: isDisabled,
          },
        ]
      : [
          {
            label: "Add New Line",
            onClick: handleAddDetailPage,
            disabled: isDisabled,
          },
        ];

  const handleAddDetailAllowance = (e) => {
    if (e) e.stopPropagation();
    const newRow = {
      TempId: `NEW-${Date.now()}`,
      LineNo: detailAllowances.length + 1,
      ACIndicator: "",
      ReferenceId: "",
      MiscAllowanceChargeCatalogId: null,
      Amount: "",
      Percent: "",
      ACCode: "",
      Description: "",
      Rate: 0,
      UoM: "",
      PostType: "",
      AgenceACCode: "",
    };
    onFieldUpdate(`DocumentDetails[${actualIndex}].MiscAllowanceCharges`, [
      ...detailAllowances,
      newRow,
    ]);
  };
  const handleDeleteDetailAllowance = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedDetailAllowanceIds, "Discount/Charge", () => {
      const updatedRows = detailAllowances.filter(
        (row) => !selectedDetailAllowanceIds.includes(getAllowanceId(row)),
      );
      const reIndexedAllowances = reIndexLines(updatedRows, "LineNo");
      onFieldUpdate(
        `DocumentDetails[${actualIndex}].MiscAllowanceCharges`,
        reIndexedAllowances,
      );
      setSelectedDetailAllowanceIds([]);
    });
  };
  const detailAllowanceActions = [
    {
      label: "Delete",
      onClick: handleDeleteDetailAllowance,
      variant: "danger",
      disabled: isDisabled,
    },
    {
      label: "Add Row",
      onClick: handleAddDetailAllowance,
      disabled: isDisabled,
    },
  ];

  const handleAddDetailTax = (e) => {
    if (e) e.stopPropagation();
    const newRow = {
      TempId: `NEW-${Date.now()}`,
      TaxCode: "",
      TaxAmount: 0,
      TaxName: "",
      CatalogId: null,
    };
    onFieldUpdate(`DocumentDetails[${actualIndex}].Taxes`, [
      ...detailTaxes,
      newRow,
    ]);
  };
  const handleDeleteDetailTax = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedDetailTaxIds, "Tax", () => {
      const updatedRows = detailTaxes.filter(
        (row) => !selectedDetailTaxIds.includes(getTaxId(row)),
      );
      onFieldUpdate(`DocumentDetails[${actualIndex}].Taxes`, updatedRows);
      setSelectedDetailTaxIds([]);
    });
  };
  const detailTaxActions = [
    {
      label: "Delete",
      onClick: handleDeleteDetailTax,
      variant: "danger",
      disabled: isDisabled,
    },
    { label: "Add Row", onClick: handleAddDetailTax, disabled: isDisabled },
  ];

  const handleAddDetailOptField = (e) => {
    if (e) e.stopPropagation();
    const newRow = {
      TempId: `NEW-${Date.now()}`,
      FieldCode: "",
      FieldName: "",
      FieldValue: "",
    };
    onFieldUpdate(`DocumentDetails[${actualIndex}].OptionalFields`, [
      ...detailOptFields,
      newRow,
    ]);
  };
  const handleDeleteDetailOptField = (e) => {
    if (e) e.stopPropagation();
    deleteConfirmation(selectedDetailOptFieldIds, "Optional Field", () => {
      const updatedRows = detailOptFields.filter(
        (row) => !selectedDetailOptFieldIds.includes(getOptionalFieldId(row)),
      );
      onFieldUpdate(
        `DocumentDetails[${actualIndex}].OptionalFields`,
        updatedRows,
      );
      setSelectedDetailOptFieldIds([]);
    });
  };
  const detailOptFieldActions = [
    {
      label: "Delete",
      onClick: handleDeleteDetailOptField,
      variant: "danger",
      disabled: isDisabled,
    },
    {
      label: "Add Row",
      onClick: handleAddDetailOptField,
      disabled: isDisabled,
    },
  ];

  //Columns in each list are defined from which values are being read from the PDF
  const allowanceChargesColumns = [
    {
      field: "LineNo",
      headerName: "Line #",
      width: 75,
      editable: false,
    },
    {
      field: "ACIndicator", //Type = Allowance or Charge
      headerName: "Type",
      width: 120,
      editable: !isDisabled,
      renderHeader: (params) => {
        return (
          <>
            <p style={{ fontWeight: "var(--font-medium)" }}>
              {params.colDef.headerName}{" "}
            </p>
            <span
              style={{
                color: "var(--error-primary)",
                verticalAlign: "super",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          </>
        );
      },
      cellClassName: (params) => {
        if (!params) return "";
        return !params.value || params.value == "" ? "error-cell" : "";
      },
      renderEditCell: (params) => {
        return (
          <Select
            className={!params.value || params.value == "" ? "error-cell" : ""}
            sx={{ width: "100%", border: "none" }}
            value={params.value}
            label=""
            onChange={(e) => {
              const newValue = e.target.value;
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue,
              });
            }}
          >
            <MenuItem value="Allowance">Allowance</MenuItem>
            <MenuItem value="Charge">Charge</MenuItem>
          </Select>
        );
      },
    },
    {
      field: "ACCode",
      headerName: "Code",
      width: 170,
      editable: !isDisabled,
      renderHeader: (params) => {
        return (
          <>
            <p style={{ fontWeight: "var(--font-medium)" }}>
              {params.colDef.headerName}{" "}
            </p>
            <span
              style={{
                color: "var(--error-primary)",
                verticalAlign: "super",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          </>
        );
      },
      cellClassName: (params) => {
        if (!params) return "";
        if (
          params.value === null ||
          (params.value === "" &&
            params.row.MiscAllowanceChargeCatalogId === null)
        ) {
          return "error-cell";
        }
        return "";
      },
      renderEditCell: (params) => (
        <GridCatalogAutocomplete
          params={params}
          catalogId={params.row.MiscAllowanceChargeCatalogId}
          isRequired={true}
          connectionId={connectionId}
          artifactType={CatalogTypes.AllowanceOrCharge}
          initialOptions={catalogOptions.allowances}
          onChange={async (newValue) => {
            let newCode = "";
            let catalogId = null;
            let indicator = params.row.ACIndicator; //no need to wipe this on change
            let acDescription = "";
            if (newValue != "" && newValue) {
              newCode = newValue.Code;
              catalogId = newValue.CatalogId;
              const artifactData = JSON.parse(newValue.ArtifactData);
              indicator = artifactData.Indicator;
              acDescription = artifactData.ACCodeDescription || "";
            }
            const rowId = params.id;
            //check header for rowId match and if not there, check detail
            const headerIndex = allowanceChargeData?.findIndex(
              (row) => getAllowanceId(row) === rowId,
            );

            if (headerIndex !== undefined && headerIndex !== -1) {
              onFieldUpdate(
                `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACCode`,
                newCode,
              );
              onFieldUpdate(
                `DocumentHeader.MiscAllowanceCharges[${headerIndex}].MiscAllowanceChargeCatalogId`,
                catalogId,
              );
              //MiscAllowanceChargeCatalogId
              onFieldUpdate(
                `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACIndicator`,
                indicator,
              );
              onFieldUpdate(
                `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACCodeDescription`,
                acDescription || "",
              );
            } else {
              const detailIndex = detailAllowances?.findIndex(
                (row) => getAllowanceId(row) === rowId,
              );

              if (detailIndex !== undefined && detailIndex !== -1) {
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].ACCode`,
                  newCode,
                );
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].MiscAllowanceChargeCatalogId`,
                  catalogId,
                );
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].ACIndicator`,
                  indicator,
                );
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].Description`,
                  acDescription || "",
                );
              }
            }
            await params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newCode,
            });
            await params.api.setEditCellValue({
              id: params.id,
              field: "ACIndicator",
              value: indicator,
            });
            await params.api.setEditCellValue({
              id: params.id,
              field: "ACCodeDescription",
              value: acDescription || "",
            });
            params.api.stopCellEditMode({
              id: params.id,
              field: params.field,
            });
            params.api.stopCellEditMode({
              id: params.id,
              field: "ACIndicator",
            });
            params.api.stopCellEditMode({
              id: params.id,
              field: "ACCodeDescription",
            });
          }}
          onAddNew={(typedValue) => {
            setActivePopupType(CatalogTypes.AllowanceOrCharge);
            setActivePopupData({
              ...params.row,
              id: params.id, //send row id so that on adding catalog, the edited row can be traced back
              ReferenceId: typedValue,
            });
          }}
        />
      ),
      renderCell: (params) => (
        <>
          {params.value == "" && params.row.MiscAllowanceChargeCatalogId
            ? "[BLANK]"
            : params.value}
        </>
      ),
    },
    {
      field: "Amount",
      headerName: "Amount",
      type: "number",
      width: 110,
      editable: !isDisabled,
    },
    {
      field: "Percent",
      headerName: "Percent %",
      type: "number",
      width: 105,
      editable: !isDisabled,
    },
    {
      field: "ReferenceId",
      headerName: "Ref ID",
      width: 100,
      editable: !isDisabled,
    },
    {
      field: "ACCodeDescription",
      headerName: "Code Description",
      width: 200,
      editable: !isDisabled,
    },
    {
      field: "Description",
      headerName: "Description",
      width: 250,
      editable: !isDisabled,
    },
    {
      field: "Rate",
      headerName: "Rate",
      type: "number",
      width: 100,
      editable: !isDisabled,
    },
    {
      field: "UoM",
      headerName: "Unit for Measurement",
      width: 90,
      editable: !isDisabled,
    },
    {
      field: "PostType",
      headerName: "Post Type",
      width: 120,
      editable: !isDisabled,
    },
    {
      field: "AgenceACCode",
      headerName: "Agency Code",
      width: 130,
      editable: !isDisabled,
    },
  ];

  const taxColumns = [
    {
      field: "TaxCode",
      headerName: "Tax Code",
      width: 120,
      editable: !isDisabled,
      renderHeader: (params) => {
        return (
          <>
            <p style={{ fontWeight: "var(--font-medium)" }}>
              {params.colDef.headerName}{" "}
            </p>
            <span
              style={{
                color: "var(--error-primary)",
                verticalAlign: "super",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          </>
        );
      },
      cellClassName: (params) => {
        if (!params) return "";
        if (
          params.value === null ||
          (params.value === "" && params.row.CatalogId === null)
        ) {
          return "error-cell";
        }
        return "";
      },
      renderEditCell: (params) => (
        <GridCatalogAutocomplete
          params={params}
          catalogId={params.row.CatalogId}
          connectionId={connectionId}
          artifactType={CatalogTypes.TaxGroup}
          initialOptions={catalogOptions.taxes}
          isRequired={true}
          onChange={async (newValue) => {
            let newCode = "";
            let catalogId = null;
            if (newValue != "" && newValue) {
              newCode = newValue.Code;
              catalogId = newValue.CatalogId;
            }

            const rowId = params.id;
            //check header for rowId match and if not there, check detail
            const headerIndex = taxData?.findIndex(
              (row) => getTaxId(row) === rowId,
            );
            if (headerIndex !== undefined && headerIndex !== -1) {
              onFieldUpdate(
                `DocumentHeader.Taxes[${headerIndex}].TaxCode`,
                newCode,
              );
              onFieldUpdate(
                `DocumentHeader.Taxes[${headerIndex}].CatalogId`,
                catalogId,
              );
            } else {
              const detailIndex = detailTaxes?.findIndex(
                (row) => getTaxId(row) === rowId,
              );

              if (detailIndex !== undefined && detailIndex !== -1) {
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].Taxes[${detailIndex}].TaxCode`,
                  newCode,
                );
                onFieldUpdate(
                  `DocumentDetails[${actualIndex}].Taxes[${detailIndex}].CatalogId`,
                  catalogId,
                );
              }
            }
            await params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: newCode,
            });
            params.api.stopCellEditMode({
              id: params.id,
              field: params.field,
            });
          }}
          onAddNew={(typedValue) => {
            setActivePopupType(CatalogTypes.TaxGroup);
            setActivePopupData({
              ...params.row,
              id: params.id, //send row id so that on adding catalog, the edited row can be traced back
              Code: typedValue,
            });
          }}
        />
      ),
      renderCell: (params) => (
        <>
          {params.value == "" && params.row.CatalogId
            ? "[BLANK]"
            : params.value}
        </>
      ),
    },
    {
      field: "TaxAmount",
      headerName: "Amount",
      type: "number",
      width: 100,
      editable: !isDisabled,
    },
    {
      field: "TaxName",
      headerName: "Tax Name",
      flex: 1,
      editable: !isDisabled,
    },
  ];

  const optFieldsColumns = [
    {
      field: "FieldCode",
      headerName: "Code",
      width: 150,
      editable: !isDisabled,
      renderHeader: (params) => {
        return (
          <>
            <p style={{ fontWeight: "var(--font-medium)" }}>
              {params.colDef.headerName}{" "}
            </p>
            <span
              style={{
                color: "var(--error-primary)",
                verticalAlign: "super",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          </>
        );
      },
      cellClassName: (params) => {
        if (!params) return "";
        return !params.value || params.value == "" ? "error-cell" : "";
      },
    },
    {
      field: "FieldName",
      headerName: "Field Name",
      flex: 1,
      editable: !isDisabled,
    },
    {
      field: "FieldValue",
      headerName: "Value",
      flex: 1,
      editable: !isDisabled,
    },
  ];

  //currently not implemented
  // const itemCompColumns = [
  //   {field: "ComponentCode", headerName: "Code", width: 100, editable: true},
  //   {field: "ComponentDescription", headerName: "Description", width: 100, editable: true},
  //   {field: "BuidQuantity", headerName: "Build Qty", type: "number", width: 100, editable: true},
  //   {field: "QuantityOrdered", headerName: "Order Qty", type: "number", width: 100, editable: true},
  //   {field: "UoM", headerName: "UOM", width: 80, editable: true},
  //   {field: "UnitCost", headerName: "Unit Cost", type: "number", width: 100, editable: true},
  // ];

  const ListGridStyle = {
    disabled: !isDisabled,
    color: "var(--text-primary)",
    fontSize: "var(--font-sm)",
    fontFamily: "var(--font-family)",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
    //backgroundColor: "var(--default-light)",
    border: "none",
    "& .MuiDataGrid:hover": { backgroundColor: "inherit" },
    "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
      opacity: 0,
      borderBottom: "none",
    },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-row:hover": { backgroundColor: "var(--tertiary-13)" },
    "& .MuiDataGrid-cell--editable:hover": {
      cursor: "text",
      backgroundColor: "var(--btn-hover-lighter)",
    },
    "& .Mui-selected": { backgroundColor: "var(--primary-pale)" },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
      { outline: "none" },
    "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
      { opacity: 1 },
    "& .Mui-selected .MuiDataGrid-cellCheckbox": { opacity: 1 },

    "& .MuiDataGrid-columnHeaderTitle": { borderLeft: "none" },
    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
      backgroundColor: "var(--primary-pale)",
      borderBottom: "none",
      borderLeft: "none",
    },
    // "& .error-cell.MuiDataGrid-cell.MuiDataGrid-cell--editing": {
    //   border: "1px solid var(--status-error-intense)",
    //   borderRadius: "5px",
    // },
    "& .error-cell": {
      border: "1px solid var(--status-error-intense)",
      borderRadius: "5px",
    },
  };

  return (
    <>
      <Box sx={{ margin: "15px" }}>
        {/* main header table */}
        <Accordion
          defaultExpanded
          sx={{
            borderRadius: "8px",
            "&:before": { display: "none" },
            boxShadow: 1,
          }}
        >
          <AccordionSummary expandIcon={<ArrowDownInactiveComponent />}>
            <Typography variant="h3">Document Header</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!isDataLoaded ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <LoadingBar isDataLoaded={false} secondsToStall={1} />
                <Box sx={{ height: "80px" }} />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: "1px solid var(--disabled)",
                    borderRadius: "8px",
                    mb: 3,
                  }}
                >
                  <Table size="small" aria-label="header details table">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "var(--font-bold)",
                            fontSize: "18px",
                            color: "var(--text-primary)",
                            borderBottom: "1px solid var(--disabled)",
                          }}
                        >
                          Field
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "var(--font-bold)",
                            fontSize: "18px",
                            color: "var(--text-primary)",
                            borderBottom: "1px solid var(--disabled)",
                          }}
                        >
                          Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <FieldPropertyRow
                        label="Purchase Order Number"
                        path="DocumentHeader.PurchaseOrderNumber"
                        value={headerData.PurchaseOrderNumber}
                        onChange={onFieldUpdate}
                        isRequired={true}
                        isReadOnly={isDisabled}
                        showErrors={showValidationErrors}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Purchase Order Date"
                        path="DocumentHeader.PurchaseOrderDate"
                        value={headerData.PurchaseOrderDate}
                        onChange={onFieldUpdate}
                        isReadOnly={isDisabled}
                        type="date"
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Purpose Code"
                        path="DocumentHeader.PurposeCode"
                        value={headerData.PurposeCode}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Purpose Description"
                        path="DocumentHeader.PurposeDescription"
                        value={headerData.PurposeDescription}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Purchase Order Type"
                        path="DocumentHeader.PurchaseOrderType"
                        value={headerData.PurchaseOrderType}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Purchase Order Type Description"
                        path="DocumentHeader.PurchaseOrderTypeDescription"
                        value={headerData.PurchaseOrderTypeDescription}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <CatalogAutocompleteRow
                        label="Customer Code"
                        path="DocumentHeader.CustomerCode"
                        value={headerData.CustomerCode}
                        isRequired={true}
                        catalogId={headerData.CustomerCatalogId}
                        showErrors={showValidationErrors}
                        onChange={(path, newValue) => {
                          if (newValue === "" || newValue === null) {
                            onFieldUpdate(path, "");
                            onFieldUpdate(
                              `DocumentHeader.CustomerCatalogId`,
                              null,
                            );
                            onFieldUpdate(`DocumentHeader.CustomerName`, "");
                          } else {
                            onFieldUpdate(path, newValue.Code);
                            onFieldUpdate(
                              `DocumentHeader.CustomerCatalogId`,
                              newValue.CatalogId,
                            );
                            const customerCode = JSON.parse(
                              newValue.ArtifactData,
                            ).CustomerName;
                            onFieldUpdate(
                              `DocumentHeader.CustomerName`,
                              customerCode,
                            );
                          }
                        }}
                        disabledOverride={isDisabled}
                        connectionId={connectionId}
                        artifactType={CatalogTypes.Customer}
                        initialOptions={catalogOptions.customers}
                      />
                      <FieldPropertyRow
                        label="Customer Name"
                        path="DocumentHeader.CustomerName"
                        value={headerData.CustomerName}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <CatalogAutocompleteRow
                        label="Ship To Code"
                        path="DocumentHeader.ShipToDetails.Code"
                        value={headerData.ShipToDetails?.Code ?? ""}
                        catalogId={headerData.ShipToDetails?.CatalogId}
                        isRequired={true}
                        showErrors={showValidationErrors}
                        onChange={(path, newValue) => {
                          if (newValue === "" || newValue === null) {
                            onFieldUpdate(path, "");
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.CatalogId`,
                              null,
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.Name`,
                              "",
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.AddressLine1`,
                              "",
                            );
                          } else {
                            onFieldUpdate(path, newValue.Code);
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.CatalogId`,
                              newValue.CatalogId,
                            );
                            const artifactData = JSON.parse(
                              newValue.ArtifactData,
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.Name`,
                              artifactData.ShipToName,
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipToDetails.AddressLine1`,
                              artifactData.Address1,
                            );
                          }
                        }}
                        disabledOverride={isDisabled}
                        connectionId={connectionId}
                        artifactType={CatalogTypes.ShipTo}
                        initialOptions={catalogOptions.shipTo}
                      />
                      <FieldPropertyRow
                        label="Ship To Name"
                        path="DocumentHeader.ShipToDetails.Name"
                        value={headerData.ShipToDetails?.Name}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Ship To Address Line 1"
                        path="DocumentHeader.ShipToDetails.AddressLine1"
                        value={headerData.ShipToDetails?.AddressLine1}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <CatalogAutocompleteRow
                        label="Carrier Code"
                        path="DocumentHeader.Carrier.Code"
                        value={headerData.Carrier?.Code ?? ""}
                        catalogId={headerData.Carrier?.CarrierCatalogId}
                        isRequired={false}
                        showErrors={showValidationErrors}
                        onChange={(path, newValue) => {
                          if (newValue === "" || newValue === null) {
                            onFieldUpdate(path, "");
                            onFieldUpdate(
                              `DocumentHeader.Carrier.CarrierCatalogId`,
                              null,
                            );
                            onFieldUpdate(`DocumentHeader.Carrier.Name`, "");
                          } else {
                            onFieldUpdate(path, newValue.Code);
                            onFieldUpdate(
                              `DocumentHeader.Carrier.CarrierCatalogId`,
                              newValue.CatalogId,
                            );
                            const artifactData = JSON.parse(
                              newValue.ArtifactData,
                            );
                            onFieldUpdate(
                              `DocumentHeader.Carrier.Name`,
                              artifactData.CarrierName,
                            );
                          }
                        }}
                        disabledOverride={isDisabled}
                        connectionId={connectionId}
                        artifactType={CatalogTypes.Carrier}
                        initialOptions={catalogOptions.carriers}
                      />
                      <FieldPropertyRow
                        label="Carrier Name"
                        path="DocumentHeader.Carrier.Name"
                        value={headerData.Carrier?.Name}
                        isReadOnly={isDisabled}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <CatalogAutocompleteRow
                        label="Ship From Code"
                        path={`DocumentHeader.ShipFrom.Code`}
                        value={headerData.ShipFrom?.Code ?? ""}
                        catalogId={headerData.ShipFrom?.CatalogId}
                        isRequired={false}
                        showErrors={showValidationErrors}
                        onChange={(path, newValue) => {
                          if (newValue === "" || newValue === null) {
                            onFieldUpdate(path, "");
                            onFieldUpdate(
                              `DocumentHeader.ShipFrom.CatalogId`,
                              null,
                            );
                            onFieldUpdate(`DocumentHeader.ShipFrom.Name`, "");
                            onFieldUpdate(
                              `DocumentHeader.ShipFrom.AddressLine1`,
                              "",
                            );
                          } else {
                            onFieldUpdate(path, newValue.Code);
                            onFieldUpdate(
                              `DocumentHeader.ShipFrom.CatalogId`,
                              newValue.CatalogId,
                            );
                            const artifactData = JSON.parse(
                              newValue.ArtifactData,
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipFrom.Name`,
                              artifactData.Name,
                            );
                            onFieldUpdate(
                              `DocumentHeader.ShipFrom.AddressLine1`,
                              artifactData.Address1,
                            );
                          }
                        }}
                        connectionId={connectionId}
                        artifactType={CatalogTypes.Warehouse}
                        initialOptions={catalogOptions.warehouses}
                      />
                      <FieldPropertyRow
                        label="Ship From Name"
                        path={`DocumentHeader.ShipFrom.Name`}
                        value={headerData.ShipFrom?.Name}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                      <FieldPropertyRow
                        label="Ship From Address Line 1"
                        path={`DocumentHeader.ShipFrom.AddressLine1`}
                        value={headerData.ShipFrom?.AddressLine1}
                        onChange={onFieldUpdate}
                        connectionId={connectionId}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* charges/allowances list */}
                <Accordion
                  sx={{
                    border: "1px solid var(--disabled)",
                    boxShadow: "var(--shadow-elevation-2)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      minHeight: "36px !important",
                      "& .MuiAccordionSummary-content": {
                        margin: "8px 0 !important",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "var(--font-bold)" }}
                    >
                      + Discounts/Charges
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pb: 1,
                      }}
                    >
                      <ShepHorizontalMenu actions={allowanceActions} />
                    </Box>

                    <Box sx={{ width: "100%" }}>
                      <ShepDataGridPro
                        autoHeight={true}
                        rows={allowanceChargeData}
                        columns={allowanceChargesColumns}
                        getRowId={getAllowanceId}
                        processRowUpdate={(newRow, oldRow) => {
                          //validate percent & amount
                          if (
                            newRow.Percent &&
                            newRow.Percent != "" &&
                            newRow.Percent < 0
                          ) {
                            newRow.Percent = 0;
                          }
                          if (
                            newRow.Amount &&
                            newRow.Amount != "" &&
                            newRow.Amount < 0
                          ) {
                            newRow.Amount = 0;
                          }

                          if (
                            newRow.Amount &&
                            newRow.Amount != "" &&
                            newRow.Percent &&
                            newRow.Percent != ""
                          ) {
                            //cannot have both set
                            //revert last changed
                            if (newRow.Amount !== oldRow.Amount) {
                              newRow.Amount = null;
                            }
                            if (newRow.Percent !== oldRow.Percent) {
                              newRow.Percent = null;
                            }
                          }
                          return handleGridEdit(
                            newRow,
                            oldRow,
                            "DocumentHeader.MiscAllowanceCharges",
                            allowanceChargeData,
                            getAllowanceId,
                          );
                        }}
                        isCellEditable={(params) => {
                          if (params.field == "LineNo") return false;
                          if (
                            params.field === "Percent" &&
                            params.row.Amount !== "" &&
                            params.row.Amount !== null
                          )
                            return false;

                          if (
                            params.field === "Amount" &&
                            params.row.Percent !== "" &&
                            params.row.Percent !== null
                          )
                            return false;

                          return true;
                        }}
                        isDataLoaded={Object.keys(artifactData).length > 0}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 5, page: 0 },
                          },
                        }}
                        slots={{
                          baseCheckbox: ShepCheckbox,
                          pagination: ShepPagination,
                        }}
                        slotProps={{
                          pagination: {
                            pageOptions: [5, 15, 30],
                          },
                        }}
                        sx={ListGridStyle}
                        checkboxSelection={true}
                        disableRowSelectionOnClick={true}
                        disableMultipleRowSelection={false}
                        onRowSelectionModelChange={(ids) =>
                          setSelectedAllowanceIds(ids)
                        }
                        rowSelectionModel={selectedAllowanceIds}
                        hideFooterSelectedRowCount={true}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableColumnReorder={true}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* taxes list */}
                <Accordion
                  sx={{
                    border: "1px solid var(--disabled)",
                    boxShadow: "var(--shadow-elevation-2)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      minHeight: "36px !important",
                      "& .MuiAccordionSummary-content": {
                        margin: "8px 0 !important",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "var(--font-bold)" }}
                    >
                      + Taxes
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pb: 1,
                      }}
                    >
                      <ShepHorizontalMenu actions={taxActions} />
                    </Box>

                    <Box sx={{ width: "100%" }}>
                      <ShepDataGridPro
                        autoHeight={true}
                        rows={taxData}
                        columns={taxColumns}
                        getRowId={getTaxId}
                        processRowUpdate={(newRow, oldRow) =>
                          handleGridEdit(
                            newRow,
                            oldRow,
                            "DocumentHeader.Taxes",
                            taxData,
                            getTaxId,
                          )
                        }
                        isDataLoaded={Object.keys(artifactData).length > 0}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 5, page: 0 },
                          },
                        }}
                        slots={{
                          baseCheckbox: ShepCheckbox,
                          pagination: ShepPagination,
                        }}
                        slotProps={{
                          pagination: {
                            pageOptions: [5, 15, 30],
                          },
                        }}
                        sx={ListGridStyle}
                        checkboxSelection={true}
                        disableRowSelectionOnClick={true}
                        disableMultipleRowSelection={false}
                        onRowSelectionModelChange={(ids) =>
                          setSelectedTaxIds(ids)
                        }
                        rowSelectionModel={selectedTaxIds}
                        hideFooterSelectedRowCount={true}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableColumnReorder={true}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* optional fields list */}
                <Accordion
                  sx={{
                    border: "1px solid var(--disabled)",
                    boxShadow: "var(--shadow-elevation-2)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      minHeight: "36px !important",
                      "& .MuiAccordionSummary-content": {
                        margin: "8px 0 !important",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "var(--font-bold)" }}
                    >
                      + Optional Fields
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pb: 1,
                      }}
                    >
                      <ShepHorizontalMenu actions={optFieldActions} />
                    </Box>

                    <Box sx={{ width: "100%" }}>
                      <ShepDataGridPro
                        autoHeight={true}
                        rows={optFieldData}
                        columns={optFieldsColumns}
                        getRowId={getOptionalFieldId}
                        processRowUpdate={(newRow, oldRow) =>
                          handleGridEdit(
                            newRow,
                            oldRow,
                            "DocumentHeader.OptionalFields",
                            optFieldData,
                            getOptionalFieldId,
                          )
                        }
                        isDataLoaded={Object.keys(artifactData).length > 0}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 5, page: 0 },
                          },
                        }}
                        slots={{
                          baseCheckbox: ShepCheckbox,
                          pagination: ShepPagination,
                        }}
                        slotProps={{
                          pagination: {
                            pageOptions: [5, 15, 30],
                          },
                        }}
                        sx={ListGridStyle}
                        checkboxSelection={true}
                        disableRowSelectionOnClick={true}
                        disableMultipleRowSelection={false}
                        onRowSelectionModelChange={(ids) =>
                          setSelectedOptFieldIds(ids)
                        }
                        rowSelectionModel={selectedOptFieldIds}
                        hideFooterSelectedRowCount={true}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableColumnReorder={true}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Document Details section starts here */}
      <Box sx={{ margin: "15px" }}>
        <Accordion
          defaultExpanded
          sx={{
            borderRadius: "8px",
            "&:before": { display: "none" },
            boxShadow: 1,
          }}
        >
          <AccordionSummary expandIcon={<ArrowDownInactiveComponent />}>
            <Typography variant="h3">Document Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!isDataLoaded ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <LoadingBar isDataLoaded={false} secondsToStall={1} />
                <Box sx={{ height: "80px" }} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <ShepSearchBar
                    placeholder="Search by Item Code or Description"
                    value={localSearchTerm}
                    onChange={(event) => {
                      const value = event.target.value;
                      setLocalSearchTerm(value);
                      if (value.trim() === "") {
                        setDetailSearchTerm("");
                        setCurrentDetailPage(1);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        setDetailSearchTerm(localSearchTerm);
                        setCurrentDetailPage(1);
                      }
                    }}
                    sx={{
                      width: "480px",
                      "&:hover fieldset": {
                        backgroundColor: "#ffffff",
                      },
                      "&.MuiFormControl-root .MuiInputBase-root": {
                        borderRadius: "5px",
                      },
                      "&:hover fieldset": {
                        color: "#000000",
                      },
                    }}
                  />
                  <ShepHorizontalMenu actions={detailPageActions} />
                </Box>

                {/* main detail table */}
                {currentDetail && actualIndex !== -1 ? (
                  <>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        border: "1px solid var(--disabled)",
                        borderRadius: "8px",
                        mb: 2,
                      }}
                    >
                      <Table size="small" aria-label="document detail table">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "var(--font-bold)",
                                fontSize: "18px",
                                color: "var(--text-primary)",
                                borderBottom: "1px solid var(--disabled)",
                              }}
                            >
                              Field
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "var(--font-bold)",
                                fontSize: "18px",
                                color: "var(--text-primary)",
                                borderBottom: "1px solid var(--disabled)",
                              }}
                            >
                              Value
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <FieldPropertyRow
                            label="Line Number"
                            path={`DocumentDetails[${actualIndex}].LineNumber`}
                            value={currentDetail.LineNumber}
                            onChange={onFieldUpdate}
                            type="number"
                            isReadOnly={true}
                            connectionId={connectionId}
                          />
                          <CatalogAutocompleteRow
                            label="Item Code"
                            path={`DocumentDetails[${actualIndex}].ItemCode`}
                            value={currentDetail.ItemCode}
                            isRequired={true}
                            showErrors={showValidationErrors}
                            onChange={(path, newValue) => {
                              if (newValue === "" || newValue === null) {
                                onFieldUpdate(path, "");
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemCatalogId`,
                                  null,
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemDescription`,
                                  "",
                                );
                              } else {
                                onFieldUpdate(path, newValue.Code);
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemCatalogId`,
                                  newValue.CatalogId,
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemDescription`,
                                  newValue.Description,
                                );
                              }
                            }}
                            catalogId={currentDetail.ItemCatalogId}
                            connectionId={connectionId}
                            artifactType={CatalogTypes.Item}
                            initialOptions={catalogOptions.items}
                            onAddNew={(typedValue) => {
                              setActivePopupType(CatalogTypes.Item);
                              setActivePopupData({ ItemCode: typedValue });
                            }}
                          />
                          <FieldPropertyRow
                            label="Description"
                            path={`DocumentDetails[${actualIndex}].ItemDescription`}
                            value={currentDetail.ItemDescription}
                            onChange={onFieldUpdate}
                            connectionId={connectionId}
                          />
                          <CatalogAutocompleteRow
                            label="Item UOM"
                            path={`DocumentDetails[${actualIndex}].UoM`}
                            value={currentDetail.UoM}
                            isRequired={true}
                            showErrors={showValidationErrors}
                            onChange={(path, newValue) => {
                              if (newValue === "" || newValue === null) {
                                onFieldUpdate(path, "");
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemUOMCatalogId`,
                                  null,
                                );
                              } else {
                                const uomCode = JSON.parse(
                                  newValue.ArtifactData,
                                ).UOM;
                                onFieldUpdate(path, uomCode);
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ItemUOMCatalogId`,
                                  newValue.CatalogId,
                                );
                              }
                            }}
                            connectionId={connectionId}
                            artifactType={CatalogTypes.ItemUOM}
                            initialOptions={catalogOptions.UOM || []}
                            onAddNew={(typedValue) => {
                              setActivePopupType(CatalogTypes.ItemUOM);
                              setActivePopupData({ UOM: typedValue });
                            }}
                            catalogId={currentDetail.ItemUOMCatalogId}
                            parentCatalogId={currentDetail.ItemCatalogId}
                            disabledOverride={
                              connectionId === -1 ||
                              !currentDetail?.ItemCode ||
                              currentDetail.ItemCode.trim() === ""
                            }
                            helperTextOverride={
                              connectionId === -1
                                ? "Please select a connection first"
                                : !currentDetail?.ItemCode ||
                                    currentDetail.ItemCode.trim() === ""
                                  ? "Please enter an Item Code first"
                                  : undefined //dont override at this point
                            }
                          />
                          <FieldPropertyRow
                            label="Quantity"
                            path={`DocumentDetails[${actualIndex}].Quantity`}
                            value={currentDetail.Quantity}
                            onChange={onFieldUpdate}
                            type="number"
                            isRequired={true}
                            showErrors={showValidationErrors}
                            connectionId={connectionId}
                          />
                          <FieldPropertyRow
                            label="Unit Price"
                            path={`DocumentDetails[${actualIndex}].UnitPrice`}
                            value={currentDetail.UnitPrice}
                            onChange={onFieldUpdate}
                            type="number"
                            isRequired={true}
                            showErrors={showValidationErrors}
                            connectionId={connectionId}
                          />
                          <FieldPropertyRow
                            label="Extended Price"
                            path={`DocumentDetails[${actualIndex}].ExtendedPrice`}
                            value={currentDetail.ExtendedPrice}
                            onChange={onFieldUpdate}
                            type="number"
                            connectionId={connectionId}
                          />
                          <CatalogAutocompleteRow
                            label="Ship From Code"
                            path={`DocumentDetails[${actualIndex}].ShipFrom.Code`}
                            value={currentDetail.ShipFrom?.Code ?? ""}
                            catalogId={currentDetail.ShipFrom?.CatalogId}
                            isRequired={false}
                            showErrors={showValidationErrors}
                            onChange={(path, newValue) => {
                              if (newValue === "" || newValue === null) {
                                onFieldUpdate(path, "");
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.CatalogId`,
                                  null,
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.Name`,
                                  "",
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.AddressLine1`,
                                  "",
                                );
                              } else {
                                onFieldUpdate(path, newValue.Code);
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.CatalogId`,
                                  newValue.CatalogId,
                                );
                                const artifactData = JSON.parse(
                                  newValue.ArtifactData,
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.Name`,
                                  artifactData.Name,
                                );
                                onFieldUpdate(
                                  `DocumentDetails[${actualIndex}].ShipFrom.AddressLine1`,
                                  artifactData.Address1,
                                );
                              }
                            }}
                            connectionId={connectionId}
                            artifactType={CatalogTypes.Warehouse}
                            initialOptions={catalogOptions.warehouses}
                          />
                          <FieldPropertyRow
                            label="Ship From Name"
                            path={`DocumentDetails[${actualIndex}].ShipFrom.Name`}
                            value={currentDetail.ShipFrom?.Name}
                            onChange={onFieldUpdate}
                            connectionId={connectionId}
                          />
                          <FieldPropertyRow
                            label="Ship From Address Line 1"
                            path={`DocumentDetails[${actualIndex}].ShipFrom.AddressLine1`}
                            value={currentDetail.ShipFrom?.AddressLine1}
                            onChange={onFieldUpdate}
                            connectionId={connectionId}
                          />
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* pagination controls */}
                    {filteredDetails.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 2,
                          mb: 3,
                        }}
                      >
                        <Pagination
                          count={filteredDetails.length}
                          page={safeCurrentPage}
                          onChange={(e, val) => {
                            setCurrentDetailPage(val);
                          }}
                          shape="rounded"
                          showFirstButton
                          showLastButton
                          sx={{
                            "& .MuiPaginationItem-root.Mui-selected": {
                              color: "var(--default-light)",
                              bgcolor: "var(--primary-main)",
                              "&:hover": {
                                bgcolor: "var(--primary-main)",
                              },
                            },
                          }}
                        />
                      </Box>
                    )}

                    {/* detail item components list: TEMP HIDDEN until feature is implemented */}
                    {/* <Accordion 
                      sx={{ 
                        border: "1px solid var(--disabled)",
                        boxShadow: "var(--shadow-elevation-2)",
                        '&:before': { display:"none" },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary 
                        sx={{
                          minHeight: '36px !important',
                          '& .MuiAccordionSummary-content': {
                            margin: '8px 0 !important'
                          }
                        }}
                      >
                      <Typography variant="h6" sx={{ fontWeight: "var(--font-bold)" }}>+ Item Components</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
                          <ShepHorizontalMenu actions={detailComponentActions} />
                        </Box>
                        <Box sx={{ width: "100%" }}>
                          <ShepDataGridPro
                            autoHeight={true}
                            rows={detailComponents}
                            columns={itemCompColumns}
                            getRowId={getComponentId}
                            processRowUpdate={(newRow, oldRow) => handleGridEdit(newRow, oldRow, `DocumentDetails[${actualIndex}].ItemComponents`, detailComponents, getComponentId)}
                            isDataLoaded={Object.keys(artifactData).length > 0}
                            pagination
                            initialState={{
                              pagination: { 
                                paginationModel: { pageSize: 5, page: 0 } 
                              },
                            }}
                            slots={{ 
                              baseCheckbox: ShepCheckbox,
                              pagination: ShepPagination
                            }}
                            slotProps={{
                              pagination: {
                                pageOptions: [5, 15, 30]
                              }
                            }}
                            sx={ListGridStyle}
                            checkboxSelection={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={false}
                            onRowSelectionModelChange={(ids) => setSelectedDetailComponentIds(ids)}
                            rowSelectionModel={selectedDetailComponentIds}
                            hideFooterSelectedRowCount={true}
                            disableColumnMenu={true}
                            disableColumnFilter={true}
                            disableColumnReorder={true}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion> */}

                    {/* detail misc allow/charges list */}
                    <Accordion
                      sx={{
                        border: "1px solid var(--disabled)",
                        boxShadow: "var(--shadow-elevation-2)",
                        "&:before": { display: "none" },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          minHeight: "36px !important",
                          "& .MuiAccordionSummary-content": {
                            margin: "8px 0 !important",
                          },
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "var(--font-bold)" }}
                        >
                          + Discounts/Charges
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pb: 1,
                          }}
                        >
                          <ShepHorizontalMenu
                            actions={detailAllowanceActions}
                          />
                        </Box>
                        <Box sx={{ width: "100%" }}>
                          <ShepDataGridPro
                            autoHeight={true}
                            rows={detailAllowances}
                            columns={allowanceChargesColumns}
                            getRowId={getAllowanceId}
                            processRowUpdate={(newRow, oldRow) => {
                              //validate percent & amount

                              if (
                                newRow.Percent &&
                                newRow.Percent != "" &&
                                newRow.Percent < 0
                              ) {
                                newRow.Percent = 0;
                              }
                              if (
                                newRow.Amount &&
                                newRow.Amount != "" &&
                                newRow.Amount < 0
                              ) {
                                newRow.Amount = 0;
                              }

                              if (
                                newRow.Amount &&
                                newRow.Amount != "" &&
                                newRow.Percent &&
                                newRow.Percent != ""
                              ) {
                                //cannot have both set
                                //revert last changed
                                if (newRow.Amount !== oldRow.Amount) {
                                  newRow.Amount = null;
                                }
                                if (newRow.Percent !== oldRow.Percent) {
                                  newRow.Percent = null;
                                }
                              }
                              return handleGridEdit(
                                newRow,
                                oldRow,
                                `DocumentDetails[${actualIndex}].MiscAllowanceCharges`,
                                detailAllowances,
                                getAllowanceId,
                              );
                            }}
                            isDataLoaded={Object.keys(artifactData).length > 0}
                            isCellEditable={(params) => {
                              if (params.field == "LineNo") return false;
                              if (
                                params.field === "Percent" &&
                                params.row.Amount !== "" &&
                                params.row.Amount !== null
                              )
                                return false;

                              if (
                                params.field === "Amount" &&
                                params.row.Percent !== "" &&
                                params.row.Percent !== null
                              )
                                return false;

                              return true;
                            }}
                            pagination
                            initialState={{
                              pagination: {
                                paginationModel: { pageSize: 5, page: 0 },
                              },
                            }}
                            slots={{
                              baseCheckbox: ShepCheckbox,
                              pagination: ShepPagination,
                            }}
                            slotProps={{
                              pagination: {
                                pageOptions: [5, 15, 30],
                              },
                            }}
                            sx={ListGridStyle}
                            checkboxSelection={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={false}
                            onRowSelectionModelChange={(ids) =>
                              setSelectedDetailAllowanceIds(ids)
                            }
                            rowSelectionModel={selectedDetailAllowanceIds}
                            hideFooterSelectedRowCount={true}
                            disableColumnMenu={true}
                            disableColumnFilter={true}
                            disableColumnReorder={true}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* detail taxes list */}
                    <Accordion
                      sx={{
                        border: "1px solid var(--disabled)",
                        boxShadow: "var(--shadow-elevation-2)",
                        "&:before": { display: "none" },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          minHeight: "36px !important",
                          "& .MuiAccordionSummary-content": {
                            margin: "8px 0 !important",
                          },
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "var(--font-bold)" }}
                        >
                          + Taxes
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pb: 1,
                          }}
                        >
                          <ShepHorizontalMenu actions={detailTaxActions} />
                        </Box>
                        <Box sx={{ width: "100%" }}>
                          <ShepDataGridPro
                            autoHeight={true}
                            rows={detailTaxes}
                            columns={taxColumns}
                            getRowId={getTaxId}
                            processRowUpdate={(newRow, oldRow) =>
                              handleGridEdit(
                                newRow,
                                oldRow,
                                `DocumentDetails[${actualIndex}].Taxes`,
                                detailTaxes,
                                getTaxId,
                              )
                            }
                            isDataLoaded={Object.keys(artifactData).length > 0}
                            pagination
                            initialState={{
                              pagination: {
                                paginationModel: { pageSize: 5, page: 0 },
                              },
                            }}
                            slots={{
                              baseCheckbox: ShepCheckbox,
                              pagination: ShepPagination,
                            }}
                            slotProps={{
                              pagination: {
                                pageOptions: [5, 15, 30],
                              },
                            }}
                            sx={ListGridStyle}
                            checkboxSelection={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={false}
                            onRowSelectionModelChange={(ids) =>
                              setSelectedDetailTaxIds(ids)
                            }
                            rowSelectionModel={selectedDetailTaxIds}
                            hideFooterSelectedRowCount={true}
                            disableColumnMenu={true}
                            disableColumnFilter={true}
                            disableColumnReorder={true}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* detail optional fields list */}
                    <Accordion
                      sx={{
                        border: "1px solid var(--disabled)",
                        boxShadow: "var(--shadow-elevation-2)",
                        "&:before": { display: "none" },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          minHeight: "36px !important",
                          "& .MuiAccordionSummary-content": {
                            margin: "8px 0 !important",
                          },
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "var(--font-bold)" }}
                        >
                          + Optional Fields
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            pb: 1,
                          }}
                        >
                          <ShepHorizontalMenu actions={detailOptFieldActions} />
                        </Box>
                        <Box sx={{ width: "100%" }}>
                          <ShepDataGridPro
                            autoHeight={true}
                            rows={detailOptFields}
                            columns={optFieldsColumns}
                            getRowId={getOptionalFieldId}
                            processRowUpdate={(newRow, oldRow) =>
                              handleGridEdit(
                                newRow,
                                oldRow,
                                `DocumentDetails[${actualIndex}].OptionalFields`,
                                detailOptFields,
                                getOptionalFieldId,
                              )
                            }
                            isDataLoaded={Object.keys(artifactData).length > 0}
                            pagination
                            initialState={{
                              pagination: {
                                paginationModel: { pageSize: 5, page: 0 },
                              },
                            }}
                            slots={{
                              baseCheckbox: ShepCheckbox,
                              pagination: ShepPagination,
                            }}
                            slotProps={{
                              pagination: {
                                pageOptions: [5, 15, 30],
                              },
                            }}
                            sx={ListGridStyle}
                            checkboxSelection={true}
                            disableRowSelectionOnClick={true}
                            disableMultipleRowSelection={false}
                            onRowSelectionModelChange={(ids) =>
                              setSelectedDetailOptFieldIds(ids)
                            }
                            rowSelectionModel={selectedDetailOptFieldIds}
                            hideFooterSelectedRowCount={true}
                            disableColumnMenu={true}
                            disableColumnFilter={true}
                            disableColumnReorder={true}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid var(--secondary-2)",
                      borderRadius: "8px",
                      p: 4,
                      textAlign: "center",
                      backgroundColor: "var(--tertiary-1)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--text-primary)",
                        fontSize: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ResultError
                        className={"status-icon"}
                        style={{ transform: "translateY(-2px)" }}
                      />
                      There must be at least one line item in each order
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      {activePopupType === CatalogTypes.Item && (
        <EmailAddItemPopup
          open={true}
          onClose={() => setActivePopupType(null)}
          onSuccess={(newData = {}) => {
            setActivePopupType(null);
            getInitialCatalogData();
            if (newData) {
              onFieldUpdate(
                `DocumentDetails[${actualIndex}].ItemCode`,
                newData.Code,
              );
              onFieldUpdate(
                `DocumentDetails[${actualIndex}].ItemCatalogId`,
                newData.CatalogId,
              );
              onFieldUpdate(
                `DocumentDetails[${actualIndex}].ItemDescription`,
                newData.Description,
              );
            }
          }}
          initialItemCode={
            activePopupData?.ItemCode || currentDetail?.ItemCode || ""
          }
          initialItemDesc={currentDetail?.ItemDescription || ""}
          connectionId={connectionId}
          connectionName={connectionName}
          artifactType={CatalogTypes.Item}
        />
      )}
      {activePopupType === CatalogTypes.ItemUOM && (
        <EmailAddItemUOMPopup
          open={true}
          onClose={() => {
            setActivePopupType(null);
            setActivePopupData(null);
          }}
          onSuccess={(newData = {}) => {
            setActivePopupType(null);
            setActivePopupData(null);
            if (newData.Code) {
              onFieldUpdate(
                `DocumentDetails[${actualIndex}].UoM`,
                newData.Code,
              );
            }
            if (newData.CatalogId) {
              onFieldUpdate(
                `DocumentDetails[${actualIndex}].ItemUOMCatalogId`,
                newData.CatalogId,
              );
            }
            if (connectionId !== -1 && currentDetail?.ItemCode) {
              fetchCatalogs(
                connectionId,
                CatalogTypes.ItemUOM,
                15,
                newData.Code,
                currentDetail?.ItemCatalogId ?? null,
              ).then((data) => {
                setCatalogOptions((prev) => ({
                  ...prev,
                  UOM: data?.Records || [],
                }));
              });
            }
          }}
          initialUOM={activePopupData?.UOM || ""}
          itemCode={currentDetail?.ItemCode || ""}
          itemId={activeItemCatalogId}
          connectionId={connectionId}
          connectionName={connectionName}
          artifactType={CatalogTypes.ItemUOM}
        />
      )}
      {activePopupType === CatalogTypes.AllowanceOrCharge && (
        <EmailAddAllowanceChargePopup
          open={true}
          onClose={() => {
            setActivePopupType(null);
            setActivePopupData(null);
          }}
          onSuccess={(newData = {}) => {
            if (
              activePopupData &&
              activePopupData.id !== undefined &&
              newData.CatalogId
            ) {
              const rowId = activePopupData.id;

              //check header for rowId match and if not there, check detail
              const headerIndex = allowanceChargeData?.findIndex(
                (row) => getAllowanceId(row) === rowId,
              );
              const artifactData = JSON.parse(newData.ArtifactData);
              if (headerIndex !== undefined && headerIndex !== -1) {
                onFieldUpdate(
                  `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACCode`,
                  newData.Code,
                );
                onFieldUpdate(
                  `DocumentHeader.MiscAllowanceCharges[${headerIndex}].MiscAllowanceChargeCatalogId`,
                  newData.CatalogId,
                );
                //MiscAllowanceChargeCatalogId
                onFieldUpdate(
                  `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACIndicator`,
                  artifactData.Indicator,
                );
                onFieldUpdate(
                  `DocumentHeader.MiscAllowanceCharges[${headerIndex}].ACCodeDescription`,
                  artifactData.ACCodeDescription || "",
                );
              } else {
                const detailIndex = detailAllowances?.findIndex(
                  (row) => getAllowanceId(row) === rowId,
                );

                if (detailIndex !== undefined && detailIndex !== -1) {
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].ACCode`,
                    newData.Code,
                  );
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].MiscAllowanceChargeCatalogId`,
                    newData.CatalogId,
                  );
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].ACIndicator`,
                    artifactData.Indicator,
                  );
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].MiscAllowanceCharges[${detailIndex}].Description`,
                    artifactData.ACCodeDescription || "",
                  );
                }
              }
            }
            setActivePopupType(null);
            setActivePopupData(null);
            getInitialCatalogData();
          }}
          initialRefId={activePopupData?.ReferenceId || ""}
          initialIndicator={activePopupData?.ACIndicator || ""}
          initialDescription={activePopupData?.Description || ""}
          connectionId={connectionId}
          connectionName={connectionName}
          artifactType={CatalogTypes.AllowanceOrCharge}
        />
      )}
      {activePopupType === CatalogTypes.TaxGroup && (
        <EmailAddTaxGroupPopup
          open={true}
          onClose={() => {
            setActivePopupType(null);
            setActivePopupData(null);
          }}
          onSuccess={(newData = {}) => {
            if (
              activePopupData &&
              activePopupData.id !== undefined &&
              newData.CatalogId
            ) {
              const rowId = activePopupData.id;

              //check header for rowId match and if not there, check detail
              const headerIndex = taxData?.findIndex(
                (row) => getTaxId(row) === rowId,
              );
              const artifactData = JSON.parse(newData.ArtifactData);
              if (headerIndex !== undefined && headerIndex !== -1) {
                onFieldUpdate(
                  `DocumentHeader.Taxes[${headerIndex}].TaxCode`,
                  newData.Code,
                );
                onFieldUpdate(
                  `DocumentHeader.Taxes[${headerIndex}].CatalogId`,
                  newData.CatalogId,
                );
              } else {
                const detailIndex = detailAllowances?.findIndex(
                  (row) => getTaxId(row) === rowId,
                );

                if (detailIndex !== undefined && detailIndex !== -1) {
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].Taxes[${detailIndex}].TaxCode`,
                    newData.Code,
                  );
                  onFieldUpdate(
                    `DocumentDetails[${actualIndex}].Taxes[${detailIndex}].CatalogId`,
                    newData.CatalogId,
                  );
                }
              }
            }
            setActivePopupType(null);
            setActivePopupData(null);
            getInitialCatalogData();
          }}
          initialCode={activePopupData?.Code || ""}
          connectionId={connectionId}
          connectionName={connectionName}
          artifactType={CatalogTypes.TaxGroup}
        />
      )}
    </>
  );
};
export { EditableArtifact };
